// Скелет протокола (045: OPN-03, OPN-04; research Р2): пока данных нет дольше 300 мс от нажатия —
// один скелет в форме протокола, а не временная страница «Таблица протокола»: строка документа
// на своём месте («Документы › папка › П-N · название» из списка «Документов», как у страницы)
// и строки таблицы в рамке таблицы. Его показывает маршрутизатор (pendingComponent маршрута —
// без своей задержки: 300 мс уже прошли) и страница, если данных нет при её появлении (с
// задержкой Skeleton, «не показывать загрузку, которой нет»).
// Модуль лёгкий — он в общем пакете приложения вместе с маршрутом: таблица (Tabulator, Tiptap)
// грузится только со страницей протокола (ADR-0035).
// 046 (T047): до 640 px скелет держит место шапки телефона — название, ленту пометок, вкладки
// и строку «Правка и строки · Фильтры · ⌕» — серыми брусками той же высоты: рамка таблицы стоит
// там же, где у протокола, и после ответа таблица не прыгает (MUST «Табеля»: «экран не должен
// прыгать»). Стили — свои (ProtocolSkeleton.css): скелет может встать раньше стилей страницы.
import { Skeleton, SkeletonBar, useIsPhone } from '@kp/ui';
import { useQuery } from '@tanstack/react-query';
import { apiQuery } from '../../api/client.ts';
import { ProtocolPath, useOpensDocuments } from './frame/ProtocolPath.tsx';
import './ProtocolSkeleton.css';

/**
 * Строки таблицы; delay — через сколько они появляются (по умолчанию — 0,3 с Skeleton). Без
 * задержки (скелет маршрута: 300 мс уже прошли) — строки в первом кадре: у Skeleton и задержка 0 —
 * таймер, первый кадр был бы рамкой без строк (ревью 045, T037; OPN-04).
 */
export function TableSkeleton({ delay }: { delay?: number } = {}) {
  const bars = Array.from({ length: 8 }, (_, index) => (
    <SkeletonBar key={index} width={`${40 + ((index * 17) % 50)}%`} />
  ));
  if (delay === 0) {
    return (
      <div
        aria-busy="true"
        aria-label="Загрузка…"
        role="status"
        className="kp-skeleton kp-pt-skeleton"
        style={{ minHeight: 360 }}
      >
        {bars}
      </div>
    );
  }
  return (
    <Skeleton
      minHeight={360}
      className="kp-pt-skeleton"
      {...(delay === undefined ? {} : { delay })}
    >
      {bars}
    </Skeleton>
  );
}

/**
 * «П-N · название» — заголовок строки документа, как у страницы протокола, из списка «Документов»
 * (общий кэш с «Документами»). Без «Документов» (022: внешний) или до ответа — null.
 */
function useListedHeading(id: string): string | null {
  const documents = useOpensDocuments();
  const list = useQuery({ ...apiQuery('manager.list'), enabled: documents });
  const node = list.data?.items.find((item) => item.id === id);
  return node?.document ? `П-${node.document.number} · ${node.title}` : null;
}

export interface ProtocolSkeletonProps {
  /** Протокол: id документа = id узла «Документов». */
  id: string;
  /** Через сколько появляются строки таблицы, мс; маршрутизатор уже выждал — 0. */
  delay?: number;
}

/**
 * До 640 px — место шапки телефона (ProtocolPhoneHeader, вкладки, строка управления; бюджет —
 * minutes.css, 046): название (из списка «Документов», иначе брусок), бруски пометок, вкладок
 * и кнопок строки управления. Бруски скрыты от программ чтения (SkeletonBar).
 */
function PhoneHeadSkeleton({ heading }: { heading: string | null }) {
  return (
    <>
      <div className="kp-pt-skel-head">
        {heading ? (
          <h1 className="kp-pt-skel-title" title={heading}>
            {heading}
          </h1>
        ) : (
          <div className="kp-pt-skel-title is-bar">
            <SkeletonBar width="64%" height={16} />
          </div>
        )}
        <div className="kp-pt-skel-strip">
          <SkeletonBar width={128} height={22} round />
          <SkeletonBar width={116} height={22} round />
          <SkeletonBar width={96} height={22} round />
        </div>
      </div>
      <div className="kp-pt-skel-nav">
        <div className="kp-pt-skel-strip">
          <SkeletonBar width={96} height={14} />
          <SkeletonBar width={84} height={14} />
          <SkeletonBar width={64} height={14} />
        </div>
        <div className="kp-pt-skel-strip">
          <SkeletonBar width={156} height={32} />
          <SkeletonBar width={92} height={32} />
          <SkeletonBar width={32} height={32} />
        </div>
      </div>
    </>
  );
}

/** Страница протокола, пока его данные идут: строка документа и строки таблицы. */
export function ProtocolSkeleton({ id, delay }: ProtocolSkeletonProps) {
  const phone = useIsPhone();
  const heading = useListedHeading(id);
  return (
    <main id="main" tabIndex={-1} className="kp-page kp-page-wide kp-pt-page kp-pt-loading">
      {/* Шире 640 px — строка документа в шапке листа (порталом), как у страницы; до 640 px —
          ссылка назад к папке. */}
      <ProtocolPath
        id={id}
        title={null}
        {...(phone
          ? {}
          : {
              current: heading ? (
                <h1 className="kp-pt-doc-title" title={heading}>
                  {heading}
                </h1>
              ) : (
                <SkeletonBar width={240} height={14} />
              ),
            })}
      />
      {phone ? (
        <PhoneHeadSkeleton heading={heading} />
      ) : (
        // Шире 640 px — место строки команд таблицы («Команды таблицы»): рамка таблицы стоит там же,
        // где у протокола, и после ответа не прыгает (приёмка 046, MUST «экран не должен прыгать»).
        <div className="kp-pt-skel-ribbon" aria-hidden="true">
          <SkeletonBar width={180} height={12} />
        </div>
      )}
      <section className="t-card kp-pt-main" aria-label="Таблица протокола">
        <TableSkeleton {...(delay === undefined ? {} : { delay })} />
      </section>
    </main>
  );
}
